import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Star, Heart, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
}

interface DisplayListProps {
  displays: Display[];
  totalDisplays?: number;
  currentPage?: number;
  totalPages?: number;
  itemsPerPage?: number;
  isLoading: boolean;
  onDisplayClick?: (display: Display) => void;
  onDisplayViewDetails?: (display: Display) => void;
  onPageChange?: (page: number) => void;
  className?: string;
}

const DisplayCard: React.FC<{
  display: Display;
  onClick?: (display: Display) => void;
  onViewDetails?: (display: Display) => void;
}> = ({ display, onClick, onViewDetails }) => {
  const getLocationString = () => {
    const parts = [display.city, display.state].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Location not specified';
  };

  return (
    <Card
      className="p-4 cursor-pointer transition-all duration-200 hover:shadow-md hover:border-primary/50 border border-border bg-card"
      onClick={() => onClick?.(display)}
    >
      <div className="flex gap-4">
        {/* Image */}
        <div className="relative w-32 h-32 flex-shrink-0">
          {display.image_url ? (
            <img
              src={display.image_url}
              alt={display.name}
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-full h-full bg-muted rounded-lg flex items-center justify-center"
            style={{ display: display.image_url ? 'none' : 'flex' }}
          >
            <MapPin className="w-4 h-4 text-muted-foreground" />
          </div>
          {display.featured && (
            <Badge className="absolute -top-1 -right-1 text-xs px-1 py-0" variant="secondary">
              ⭐
            </Badge>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-sm line-clamp-2 text-foreground">
              {display.name}
            </h3>
          </div>

          <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
            <MapPin className="w-3 h-3 inline mr-1" />
            {getLocationString()}
          </p>

          {display.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {display.description}
            </p>
          )}

          {/* Tags */}
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {display.holiday_type && (
              <Badge variant="outline" className="text-xs px-1 py-0">
                {display.holiday_type}
              </Badge>
            )}
            {display.display_type && (
              <Badge variant="outline" className="text-xs px-1 py-0">
                {display.display_type}
              </Badge>
            )}
          </div>

          {/* Bottom row */}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              {display.upvote_count > 0 && (
                <span className="flex items-center gap-1">
                  <Heart className="w-3 h-3" />
                  {display.upvote_count}
                </span>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-6 px-2"
              onClick={(e) => {
                e.stopPropagation();
                onViewDetails?.(display);
              }}
            >
              Details
              <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const LoadingSkeleton: React.FC = () => (
  <div className="space-y-3">
    {[...Array(5)].map((_, i) => (
      <Card key={i} className="p-4 animate-pulse">
        <div className="flex gap-3">
          <div className="w-16 h-16 bg-muted rounded-lg flex-shrink-0" />
          <div className="flex-1">
            <div className="h-4 bg-muted rounded mb-2" />
            <div className="h-3 bg-muted rounded mb-2 w-3/4" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        </div>
      </Card>
    ))}
  </div>
);

export const DisplayList: React.FC<DisplayListProps> = ({
  displays,
  totalDisplays,
  currentPage = 1,
  totalPages = 1,
  itemsPerPage = 20,
  isLoading,
  onDisplayClick,
  onDisplayViewDetails,
  onPageChange,
  className = ""
}) => {
  const showPagination = totalPages > 1 && onPageChange;
  const startItem = totalDisplays ? (currentPage - 1) * itemsPerPage + 1 : 1;
  const endItem = totalDisplays ? Math.min(currentPage * itemsPerPage, totalDisplays) : displays.length;
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center h-64 text-center p-4">
      <MapPin className="w-12 h-12 text-muted-foreground mb-4" />
      <h3 className="font-medium text-foreground mb-2">No displays found</h3>
      <p className="text-sm text-muted-foreground max-w-xs">
        Try adjusting your filters or check back later for new displays
      </p>
    </div>
  );

  return (
    <div className={`h-full flex flex-col bg-background ${className}`}>
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              Light Displays
            </h2>
            <p className="text-sm text-muted-foreground">
              {isLoading ? 'Loading...' :
                totalDisplays ? `Showing ${startItem}-${endItem} of ${totalDisplays}` :
                `${displays.length} display${displays.length !== 1 ? 's' : ''} found`}
            </p>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {isLoading ? (
          <LoadingSkeleton />
        ) : displays.length === 0 ? (
          <EmptyState />
        ) : (
          displays.map((display) => (
            <DisplayCard
              key={display.id}
              display={display}
              onClick={onDisplayClick}
              onViewDetails={onDisplayViewDetails}
            />
          ))
        )}
      </div>

      {/* Pagination controls */}
      {!isLoading && showPagination && (
        <div className="p-4 border-t bg-background">
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="flex items-center gap-1"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {/* Show page numbers */}
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNumber: number;
                if (totalPages <= 5) {
                  // Show all pages if 5 or less
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  // Show first 5 pages
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  // Show last 5 pages
                  pageNumber = totalPages - 4 + i;
                } else {
                  // Show current page in middle
                  pageNumber = currentPage - 2 + i;
                }

                return (
                  <Button
                    key={pageNumber}
                    variant={currentPage === pageNumber ? "default" : "outline"}
                    size="sm"
                    onClick={() => onPageChange(pageNumber)}
                    className="min-w-[32px] h-8 px-2"
                  >
                    {pageNumber}
                  </Button>
                );
              })}

              {/* Show ellipsis and last page if needed */}
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <>
                  <span className="text-muted-foreground px-1">...</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(totalPages)}
                    className="min-w-[32px] h-8 px-2"
                  >
                    {totalPages}
                  </Button>
                </>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="flex items-center gap-1"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};